import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { AccountGroup, AccountGroupService, ProjectionFilter } from '@galaxy/account-group';
import { Salesperson, SalespersonService } from '@vendasta/sales';
import {
  DurationPeriod,
  ListSalesOrderRequestFilters,
  ListSalesOrderRequestSortDirection,
  ListSalesOrderRequestSortField,
  ListSalesOrderRequestSortOption,
  Order,
  SalesOrdersService,
  Status,
} from '@vendasta/sales-orders';
import { combineLatest, Observable, of, retry } from 'rxjs';
import { catchError, map, shareReplay, switchMap, take } from 'rxjs/operators';
import { CreateOrderDialogComponent, CreateOrderDialogOutput } from './create-order-dialog.component';

export type CreateRedirectURLFn = (businessId: string, orderId: string) => string;

@Injectable()
export class CreateOrderDialogService {
  constructor(
    private router: Router,
    private dialog: MatDialog,
    private accountGroupService: AccountGroupService,
    private salespersonService: SalespersonService,
    private salesOrdersService: SalesOrdersService,
  ) {}

  createOrderAndRedirect(
    partnerId: string,
    marketId: string,
    businessId: string,
    userId: string,
    createRedirectURLFn: CreateRedirectURLFn,
    skipDraftPrompt = false,
  ): Observable<string> {
    const createOrderResp$ = this.onCreateOrderDialog(partnerId, marketId, businessId, userId, skipDraftPrompt).pipe(
      shareReplay(1),
    );
    return createOrderResp$.pipe(
      map((orderId) => {
        if (!orderId) {
          return null;
        }
        const redirectURL = createRedirectURLFn(businessId, orderId);
        this.router.navigateByUrl(redirectURL);
        return orderId;
      }),
      take(1),
    );
  }

  private fetchBusiness(businessId: string): Observable<AccountGroup> {
    return this.accountGroupService.get(
      businessId,
      new ProjectionFilter({
        napData: true,
      }),
    );
  }

  private fetchSalesperson(partnerId: string, userId: string): Observable<Salesperson> {
    return this.salespersonService.getSalespersonByUserId(partnerId, userId).pipe(
      catchError((err) => {
        // Not all users have a salesperson, and we only want the salesperson to assign to the order.
        if (err.status === 404) {
          return of(new Salesperson());
        }
        throw err;
      }),
    );
  }

  private fetchLatestOrder(partnerId: string, businessId: string): Observable<Order> {
    const filters = new ListSalesOrderRequestFilters({
      businessId,
      statuses: [Status.DRAFTED],
    });
    const sort = new ListSalesOrderRequestSortOption({
      field: ListSalesOrderRequestSortField.CREATED,
      direction: ListSalesOrderRequestSortDirection.DESCENDING,
    });
    return this.salesOrdersService.list(partnerId, filters, sort, '', 1).pipe(
      map((resp) => {
        if (!resp?.results?.length) {
          return null;
        }
        return resp.results[0];
      }),
    );
  }

  private createDraftOrder(
    partnerId: string,
    marketId: string,
    businessId: string,
    userId: string,
  ): Observable<string> {
    return this.fetchSalesperson(partnerId, userId).pipe(
      map((salesperson) => salesperson?.salespersonId ?? ''),
      switchMap((salespersonId) => {
        return this.salesOrdersService
          .createDraft({
            partnerId,
            marketId,
            businessId,
            salespersonId,
            requestedActivation: new Date(),
            contractDuration: {
              value: 1,
              duration: DurationPeriod.YEAR,
            },
          })
          .pipe(retry({ delay: 1000, count: 5 }));
      }),
    );
  }

  private onCreateOrderDialog(
    partnerId: string,
    marketId: string,
    businessId: string,
    userId: string,
    skipDraftPrompt = false,
  ): Observable<string> {
    const business$ = this.fetchBusiness(businessId).pipe(shareReplay(1));
    const lastestOrder$ = this.fetchLatestOrder(partnerId, businessId).pipe(shareReplay(1));
    const latestOrderId$ = lastestOrder$.pipe(map((latestOrder) => latestOrder?.orderId));
    const draftOrderId$ = this.createDraftOrder(partnerId, marketId, businessId, userId).pipe(shareReplay(1));
    if (skipDraftPrompt) {
      return draftOrderId$;
    }

    const userResp$: Observable<CreateOrderDialogOutput> = combineLatest([business$, lastestOrder$]).pipe(
      switchMap(([business, latestOrder]) => {
        // If there are no draft orders, automatically create one.
        if (!latestOrder) {
          return of('new-order');
        }
        return this.dialog
          .open(CreateOrderDialogComponent, {
            width: '500px',
            autoFocus: false,
            data: {
              business: business,
              order: latestOrder,
            },
          })
          .afterClosed();
      }),
    );
    return userResp$.pipe(
      switchMap((resp) => {
        switch (resp) {
          case 'new-order':
            return draftOrderId$;
          case 'latest-order':
            return latestOrderId$;
          default:
            return of(null);
        }
      }),
      map((chosenOrderId) => {
        return chosenOrderId ?? null;
      }),
    );
  }
}
